/* ==========================================================================
   Cross-selling for cart drawer + cart page.
   - Pulls Shopify "related" recommendations for the products in the cart,
     aggregates them across cart items (frequency = bought-together signal),
     excludes items already in the cart, renders the cards.
   - Variant <select> swaps image + price + add target.
   - "+" adds to cart and reliably refreshes the drawer / cart page.
   ========================================================================== */
(function () {
  "use strict";

  var SECTION = "cross-sell-products";
  var SEED_LIMIT = 3;        // how many distinct cart products to seed recs from
  var built = new WeakSet(); // wraps already populated
  var cache = {};            // last rendered cards HTML per context (anti-flicker)

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }
  function addUrl() {
    return (typeof cartAddUrl !== "undefined" && cartAddUrl) || root() + "cart/add.js";
  }
  function cartJsonUrl() {
    return (typeof cartUrl !== "undefined" && cartUrl) || root() + "cart.js";
  }

  function getCart() {
    return fetch(cartJsonUrl(), { headers: { Accept: "application/json" } }).then(function (r) {
      return r.json();
    });
  }

  function fetchRecs(productId, limit, intent) {
    var url =
      root() + "recommendations/products?product_id=" + productId +
      "&limit=" + limit + "&intent=" + intent +
      "&section_id=" + SECTION;
    return fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        return Array.prototype.slice.call(doc.querySelectorAll("[data-cs-card]"));
      })
      .catch(function () { return []; });
  }

  function hide(wrap, ctx) {
    // Keep the wrap flagged as built: an empty result must NOT trigger a
    // rebuild on the next mutation (that would loop). Cart changes replace the
    // wrap with a fresh node, which is what allows a later re-render.
    wrap.classList.add("is-empty");
    var c = wrap.querySelector("[data-cross-sell-content]");
    if (c) c.innerHTML = "";
    if (ctx) cache[ctx] = "";
  }

  function build(wrap) {
    if (!wrap || built.has(wrap)) return;
    built.add(wrap); // claim early to avoid double-builds from the observer
    var content = wrap.querySelector("[data-cross-sell-content]");
    if (!content) { built.delete(wrap); return; }
    var limit = parseInt(wrap.getAttribute("data-limit"), 10) || 8;
    var intent = wrap.getAttribute("data-intent") || "related";
    var ctx = wrap.getAttribute("data-context") || "drawer";

    // Anti-flicker: when the cart drawer/page re-renders, the wrap is a fresh,
    // empty (hidden) node. Inject the last-known cards immediately so the
    // section never blinks out; the async fetch below then swaps in fresh ones.
    if (cache[ctx]) {
      content.innerHTML = cache[ctx];
      wrap.classList.remove("is-empty");
    }

    getCart().then(function (cart) {
      if (!cart || !cart.item_count) { hide(wrap, ctx); return; }
      var cartIds = [];
      cart.items.forEach(function (i) {
        if (cartIds.indexOf(i.product_id) === -1) cartIds.push(i.product_id);
      });
      var seeds = cartIds.slice(0, SEED_LIMIT);
      var perFetch = Math.min(10, Math.max(limit, 6));

      Promise.all(seeds.map(function (id) { return fetchRecs(id, perFetch, intent); }))
        .then(function (lists) {
          // merge: score = how many cart products recommend it, keep first DOM node
          var map = {};
          var order = 0;
          lists.forEach(function (cards) {
            cards.forEach(function (card) {
              var pid = parseInt(card.getAttribute("data-product-id"), 10);
              if (cartIds.indexOf(pid) !== -1) return; // already in cart
              if (map[pid]) { map[pid].score++; }
              else { map[pid] = { el: card, score: 1, order: order++ }; }
            });
          });
          var items = Object.keys(map).map(function (k) { return map[k]; });
          items.sort(function (a, b) { return b.score - a.score || a.order - b.order; });
          items = items.slice(0, limit);

          if (!items.length) { hide(wrap, ctx); return; }
          content.innerHTML = "";
          items.forEach(function (it) { content.appendChild(it.el); });
          wrap.classList.remove("is-empty");
          cache[ctx] = content.innerHTML; // remember for anti-flicker bridge
        })
        .catch(function () { if (!cache[ctx]) hide(wrap, ctx); });
    }).catch(function () { if (!cache[ctx]) hide(wrap, ctx); });
  }

  /* ---- Free shipping progress bar -------------------------------------- */
  var barsDone = new WeakSet();

  function updateBar(bar, cart) {
    var raw = (bar.getAttribute("data-threshold") || "").replace(",", ".").replace(/[^0-9.]/g, "");
    var threshold = Math.round(parseFloat(raw) * 100);
    if (!threshold || threshold <= 0) { bar.style.display = "none"; return; }
    var total = cart.total_price; // cents
    var pct = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
    var remaining = Math.max(0, threshold - total);
    var complete = total >= threshold;

    var textEl = bar.querySelector("[data-cs-fsb-text]");
    if (textEl) {
      if (complete) {
        textEl.textContent = bar.getAttribute("data-success") || "";
      } else {
        var money =
          window.Shopify && Shopify.formatMoney
            ? Shopify.formatMoney(remaining, typeof moneyFormat !== "undefined" ? moneyFormat : "")
            : (remaining / 100).toFixed(2);
        textEl.innerHTML = (bar.getAttribute("data-text") || "").replace(
          /\|\|amount\|\|/g,
          '<span class="cs-fsb__amount">' + money + "</span>"
        );
      }
    }
    var barEl = bar.querySelector("[data-cs-fsb-bar]");
    if (barEl) {
      // next frame so the width transition (the "moving beam") animates in
      (window.requestAnimationFrame || function (f) { f(); })(function () {
        barEl.style.width = pct + "%";
      });
    }
    bar.classList.toggle("is-complete", complete);

    // Weight limit: show hint when cart exceeds the configured kg limit.
    var weightEl = bar.querySelector("[data-cs-fsb-weight]");
    if (weightEl) {
      var rawW = (bar.getAttribute("data-weight-limit") || "").replace(",", ".").replace(/[^0-9.]/g, "");
      var limitG = parseFloat(rawW) * 1000; // kg -> grams
      var cartWeight = typeof cart.total_weight === "number" ? cart.total_weight : 0; // grams
      var overWeight = limitG > 0 && cartWeight > limitG;
      weightEl.hidden = !overWeight;
      bar.classList.toggle("is-overweight", overWeight);
    }
  }

  function processBars() {
    var bars = document.querySelectorAll("[data-cs-fsb]");
    if (!bars.length) return;
    var pending = [];
    bars.forEach(function (b) { if (!barsDone.has(b)) { barsDone.add(b); pending.push(b); } });
    if (!pending.length) return;
    getCart()
      .then(function (cart) {
        if (cart) pending.forEach(function (bar) { updateBar(bar, cart); });
      })
      .catch(function () { pending.forEach(function (b) { barsDone.delete(b); }); });
  }

  function scan() {
    document.querySelectorAll("[data-cross-sell]").forEach(function (wrap) {
      if (!built.has(wrap)) build(wrap);
    });
    processBars();
  }

  /* ---- Variant <select> change: swap image, price, add target ----------- */
  document.addEventListener("change", function (e) {
    var sel = e.target.closest && e.target.closest("[data-cs-variants]");
    if (!sel) return;
    var card = sel.closest("[data-cs-card]");
    if (!card) return;
    var json = card.querySelector("[data-cs-json]");
    if (!json) return;
    var data;
    try { data = JSON.parse(json.textContent); } catch (err) { return; }
    var v = (data.variants || []).filter(function (x) { return String(x.id) === String(sel.value); })[0];
    if (!v) return;

    var addBtn = card.querySelector("[data-cs-add]");
    if (addBtn) {
      addBtn.setAttribute("data-variant-id", v.id);
      addBtn.disabled = !v.available;
    }
    if (v.image) {
      var img = card.querySelector("[data-cs-image]");
      if (img) { img.src = v.image; img.removeAttribute("srcset"); }
    }
    var priceEl = card.querySelector("[data-cs-price]");
    if (priceEl) {
      if (v.compare) {
        priceEl.innerHTML =
          '<span class="cs-card__price-sale">' + v.price + "</span>" +
          '<s class="cs-card__price-compare">' + v.compare + "</s>";
      } else {
        priceEl.innerHTML = '<span class="cs-card__price-regular">' + v.price + "</span>";
      }
    }
    var unitEl = card.querySelector("[data-cs-unit]");
    if (unitEl) {
      if (v.unit) {
        unitEl.textContent = v.unit;
        unitEl.classList.remove("is-hidden");
      } else {
        unitEl.textContent = "";
        unitEl.classList.add("is-hidden");
      }
    }
  });

  /* ---- "+" add to cart -------------------------------------------------- */
  function updateHeaderCount(cart) {
    var n = cart.item_count;
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.style.display = n > 0 ? "" : "none";
      el.textContent = n > 0 && n < 100 ? n : "";
    });
    if (typeof favicon !== "undefined" && favicon && favicon.badge) {
      try { favicon.badge(n); } catch (e) {}
    }
    try { window.cartItemsCount = n; } catch (e) {}
  }

  document.addEventListener("click", function (e) {
    var btn = e.target.closest && e.target.closest("[data-cs-add]");
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var id = btn.getAttribute("data-variant-id");
    if (!id) return;

    var wrap = btn.closest("[data-cross-sell]");
    var ctx = wrap ? wrap.getAttribute("data-context") : "";
    btn.classList.add("is-loading");

    fetch(addUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error("add failed");
        return getCart();
      })
      .then(function (cart) {
        updateHeaderCount(cart);
        btn.classList.remove("is-loading");
        btn.classList.add("is-added");
        setTimeout(function () { btn.classList.remove("is-added"); }, 1200);

        // Refresh the right surface so the cart updates automatically.
        if (ctx === "page" && typeof window.cartPageUpdate === "function") {
          window.cartPageUpdate(cart);
        } else if (typeof window.buildCart === "function") {
          // refresh + (re)open the drawer; rebuilds cross-sell via observer
          window.buildCart(cart, true);
        } else if (typeof window.cartPageUpdate === "function") {
          window.cartPageUpdate(cart);
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        btn.classList.remove("is-loading");
        window.location.reload();
      });
  });

  /* ---- Boot + watch for drawer/page cart re-renders --------------------- */
  function boot() {
    scan();
    var scheduled = false;
    var obs = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      (window.requestAnimationFrame || window.setTimeout)(function () {
        scheduled = false;
        scan();
      }, 50);
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
